import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ControllerRenderProps } from "react-hook-form";
import { numberToMoney } from "@/util/moneyConversion";

type CurrencyInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  value?: number | string;
  onValueChange?: (value: number) => void;
} & Partial<Pick<ControllerRenderProps, "onChange" | "onBlur" | "value">>;

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onChange,
  onBlur,
  onValueChange,
  className,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState(() =>
    numberToMoney(Number(value) || 0)
  );

  useEffect(() => {
    setDisplayValue(numberToMoney(Number(value) || 0));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove all non-numeric characters
    const inputVal = e.target.value.replace(/\D/g, "");

    // Limit input to 15 digits
    const sanitizedInput = inputVal.slice(0, 15);

    // Convert to number (in cents)
    const numericValue = parseInt(sanitizedInput, 10) || 0;

    // Calculate decimal value
    const decimalValue = numericValue / 100;

    // Update display using the shared formatter
    setDisplayValue(numberToMoney(decimalValue));

    // Handle react-hook-form onChange
    if (onChange) {
      onChange(decimalValue);
    }

    // Handle custom onValueChange
    if (onValueChange) {
      onValueChange(decimalValue);
    }
  };

  return (
    <Input
      {...props}
      value={displayValue}
      onChange={handleChange}
      onBlur={onBlur}
      className={cn("w-[130px]", className)}
      inputMode="numeric"
    />
  );
};

export default CurrencyInput;
